import { useState, useRef, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SendIcon from "@mui/icons-material/Send";

import AppDialog from "../General/Dialog/AppDialog";
import { useToast } from "../../context/ToastContext";
import {
  getNotificationCertificateByBarcode,
  massChangeNotificationCertificateToSent,
} from "../../services/NotificationCertificateService"; // Ajusta tu ruta

// Función helper para obtener la hora de Argentina en formato para el input
const getArgentinaCurrentDateTime = () => {
  const date = new Date();
  // El locale 'sv-SE' (sueco) devuelve por defecto el formato ideal: YYYY-MM-DD HH:mm:ss
  const localString = date.toLocaleString("sv-SE", {
    timeZone: "America/Argentina/Buenos_Aires",
  });
  // Reemplazamos el espacio por la "T" y cortamos los segundos para que quede YYYY-MM-DDTHH:mm
  return localString.replace(" ", "T").substring(0, 16);
};

export default function ScannerNotificationCertificate() {
  const { showToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [barcodeInput, setBarcodeInput] = useState("");
  const [scannedItems, setScannedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estado para el modal de confirmación masiva
  const [openDialog, setOpenDialog] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form, setForm] = useState({
    status_date: "",
    notes: "",
  });

  const inputRef = useRef(null);

  // Mantener el foco en el input para que el lector siempre escriba ahí
  useEffect(() => {
    if (inputRef.current && !openDialog) {
      inputRef.current.focus();
    }
  }, [openDialog, scannedItems]);

  const handleScan = async (e) => {
    // El lector de códigos de barras suele disparar "Enter" al final
    if (e.key === "Enter") {
      e.preventDefault();
      const code = barcodeInput.trim();
      setBarcodeInput(""); // Limpiar rápido para el próximo escaneo      setSuccess("");

      if (!code) return;

      // Evitar duplicados
      if (scannedItems.some((item) => item.bottom_barcode_code === code)) {
        showToast(`La cédula con código ${code} ya está en la lista.`, "info")
        return;
      }

      try {
        setLoading(true);
        const res = await getNotificationCertificateByBarcode(code);

        if (res.data?.status === "success") {
          // Agregar al principio de la lista
          setScannedItems((prev) => [res.data.data, ...prev]);
        }
      } catch (error) {
        showToast(
          error.response?.data?.message || `Error al buscar el código: ${code}`,
        "error")
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRemove = (idToRemove) => {
    setScannedItems((prev) => prev.filter((item) => item.id !== idToRemove));
  };

  const handleOpenDialog = () => {
    if (scannedItems.length === 0) return;    setForm({
      status_date: getArgentinaCurrentDateTime(), // Usamos nuestra nueva función
      notes: "",
    });
    setOpenDialog(true);
  };

  const handleSubmitMassive = async () => {
    if (!form.status_date) {
      showToast("La fecha del estado es obligatoria.", "info")
      return;
    }

    try {
      setSubmitLoading(true);
      const payload = {
        ids: scannedItems.map((item) => item.id),
        status_date: form.status_date,
        notes: form.notes,
      };

      const res = await massChangeNotificationCertificateToSent(payload);

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setScannedItems([]); // Limpiar la lista tras el éxito
        setOpenDialog(false);
      }
    } catch (error) {
      showToast(
        error.response?.data?.message || "Error al actualizar las cédulas.",
      "error")
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.75)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                bgcolor: "primary.main",
                color: "#fff",
              }}
            >
              <QrCodeScannerIcon fontSize="large" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
                Escáner de Cédulas
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Utilizá el lector de códigos de barras para agregar cédulas a la
                lista y marcarlas como Enviadas.
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            autoFocus
            inputRef={inputRef}
            label="Escanear código de barras inferior..."
            variant="outlined"
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={handleScan}
            disabled={loading}
            placeholder="Esperando lectura del escáner..."
            InputProps={{
              sx: { fontSize: 24, fontWeight: 700, letterSpacing: 2, py: 1 },
            }}
            helperText="Asegurate de que el cursor esté parpadeando aquí antes de escanear."
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
            background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "rgba(0,0,0,0.02)",
            }}
          >
            <Typography sx={{ fontWeight: 800 }}>
              Cédulas listas para enviar ({scannedItems.length})
            </Typography>
            <Button
              variant="contained"
              color="warning"
              startIcon={<SendIcon />}
              disabled={scannedItems.length === 0}
              onClick={handleOpenDialog}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              Marcar como Enviadas
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>
                    Cód. Cédula de Notificación
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>
                    Cód. Expediente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>
                    Cód. de Barra Inferior
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Demandado</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Estado Actual</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scannedItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ py: 4, opacity: 0.6 }}
                    >
                      No hay cédulas escaneadas todavía.
                    </TableCell>
                  </TableRow>
                ) : (
                  scannedItems.map((item) => {
                    const partyName =
                      item.legalFile?.parties?.find(
                        (p) => p.role === "DEMANDADO PRINCIPAL",
                      )?.name || "-";
                    const partyLastname =
                      item.legalFile?.parties?.find(
                        (p) => p.role === "DEMANDADO PRINCIPAL",
                      )?.lastname || "";

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {item.code}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {item.extracted_legal_file_code}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {item.bottom_barcode_code}
                        </TableCell>
                        <TableCell>
                          {partyLastname} {partyName}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={item.status_text || item.status}
                            color="info"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            onClick={() => handleRemove(item.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Modal de confirmación masiva */}
      <AppDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title="Cambio masivo a ENVIADAS"
        subtitle={`Vas a marcar ${scannedItems.length} cédulas como enviadas. Completá los datos del estado.`}
        maxWidth="sm"
        actions={
          <>
            <Button
              onClick={() => setOpenDialog(false)}
              disabled={submitLoading}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSubmitMassive}
              disabled={submitLoading}
            >
              {submitLoading ? "Procesando..." : "Confirmar Envío"}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Fecha de Envío"
              type="datetime-local"
              value={form.status_date}
              onChange={(e) =>
                setForm({ ...form, status_date: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notas u observaciones (Opcional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Ej: Entregadas al notificador Juan Pérez..."
            />
          </Grid>
        </Grid>
      </AppDialog>
    </Grid>
  );
}
